import {
  ADULT_SIZES,
  CHILD_SIZES,
  MAX_CHILD_SIZE,
  MIN_ADULT_SIZE,
  ONE_SIZE,
  STANDARD_SIZES,
  WORD_SIZE_MAP,
} from './SizePills.constants';

const extractSizeIdentifiers = (sizeString: string): string[] => {
  if (!sizeString) return [];

  let cleaned = sizeString.trim();

  const parenIndex = cleaned.indexOf('(');
  if (parenIndex > 0) {
    cleaned = cleaned.substring(0, parenIndex).trim();
  }

  const dashMatch = cleaned.match(/^([A-Za-z/]+)\s*-\s*\d+/);
  if (dashMatch) {
    cleaned = dashMatch[1].trim();
  }

  if (cleaned.includes('/')) {
    return cleaned
      .split('/')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }

  return [cleaned];
};

const normalizeSize = (size: string): string => {
  const trimmed = size.trim();
  if (!trimmed) return trimmed;

  const lowerSize = trimmed.toLowerCase();

  if (WORD_SIZE_MAP[lowerSize]) {
    return WORD_SIZE_MAP[lowerSize];
  }

  const numericLetterMatch = trimmed.match(/^(\d+)([Xx][Ll]+)$/i);
  if (numericLetterMatch) {
    const num = parseInt(numericLetterMatch[1], 10);
    const letters = numericLetterMatch[2].toUpperCase();
    if (num === 2 && letters === 'XL') return 'XXL';
    if (num === 3 && letters === 'XL') return 'XXXL';
  }

  if (
    lowerSize === 'onesize' ||
    lowerSize === 'one size' ||
    lowerSize === 'one-size'
  ) {
    return 'OneSize';
  }

  if (/^[A-Za-z]+$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  return trimmed;
};

export const parseAvailableSizes = (
  availableSizes: string | string[] | null | undefined
): string[] => {
  if (!availableSizes) return [];

  const allSizes: string[] = [];

  if (Array.isArray(availableSizes)) {
    availableSizes.forEach((size) => {
      const identifiers = extractSizeIdentifiers(String(size));
      identifiers.forEach((id) => {
        const normalized = normalizeSize(id);
        if (normalized.length > 0) {
          allSizes.push(normalized);
        }
      });
    });
    return allSizes;
  }

  const sizeString = String(availableSizes);
  if (!sizeString.trim()) return [];

  sizeString.split(',').forEach((size) => {
    const identifiers = extractSizeIdentifiers(size);
    identifiers.forEach((id) => {
      const normalized = normalizeSize(id);
      if (normalized.length > 0) {
        allSizes.push(normalized);
      }
    });
  });

  return allSizes;
};

export const getSizeArray = (parsedSizes: string[]): string[] => {
  if (parsedSizes.length === 0) return [];

  if (
    parsedSizes.length === 1 &&
    (parsedSizes[0].toLowerCase() === 'onesize' ||
      parsedSizes[0].toLowerCase() === 'one size')
  ) {
    return ONE_SIZE;
  }

  const allNumericChild = parsedSizes.every((size) => {
    const num = Number(size);
    return !isNaN(num) && num <= MAX_CHILD_SIZE;
  });

  if (allNumericChild && parsedSizes.length > 0) {
    return CHILD_SIZES;
  }

  const allNumericAdult = parsedSizes.every((size) => {
    const num = Number(size);
    return !isNaN(num) && num >= MIN_ADULT_SIZE;
  });

  if (allNumericAdult && parsedSizes.length > 0) {
    return ADULT_SIZES;
  }

  return STANDARD_SIZES;
};

export const isSizeAvailable = (
  size: string,
  availableSizes: string[]
): boolean => {
  const normalizedSize = normalizeSize(size);
  return availableSizes.some(
    (available) => normalizeSize(available) === normalizedSize
  );
};
